import {
  normalizeTaskReflectionPayload,
  normalizeTaskReflectionText,
} from '../../utils/taskReflectionNormalize';
import { supabase } from './supabaseClient';

const activities_table_name = 'ml_tasks_staging';

// Store active subscriptions
const activeSubscriptions = new Map();

/**
 * Fetch all activities for the current user
 * @param {Object} filters - Optional filters { completed, urgencyLevel }
 * @returns {Promise<Object>} { data: activities[], error }
 */
export const fetchUserActivities = async (filters = {}) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  let query = supabase
    .from(activities_table_name)
    .select('*')
    .eq('user_id', session.user.id)
    .order('task_deadline', { ascending: true });

  // Apply filters
  if (filters.completed !== undefined) {
    query = query.eq('completed', filters.completed);
  }
  if (filters.urgencyLevel) {
    query = query.eq('urgency_level', filters.urgencyLevel);
  }

  const { data, error } = await query;

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[fetchUserActivities] Error fetching activities:', error);
    return { error };
  }

  return { data };
};

/**
 * Fetch a single activity by ID
 * @param {string} activityId - Activity ID
 * @returns {Promise<Object>} { data: activity, error }
 */
export const fetchActivityById = async (activityId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .select('*')
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[fetchActivityById] Error fetching activity:', error);
    return { error };
  }

  return { data };
};

/**
 * Create a new activity
 * @param {Object} activityData - Activity data
 * @returns {Promise<Object>} { data: activity, error }
 */
export const createActivity = async (activityData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const activity = {
    user_id: session.user.id,
    task_owner: activityData.who || activityData.owner || '',
    task_title: activityData.what || activityData.title || '',
    task_description: activityData.taskDescription || activityData.description || '',
    task_deadline: activityData.when || activityData.deadline || null,
    task_urgency_level: activityData.urgencyLevel || 'medium',
    task_reflection: activityData.reflection || '',
    task_conversation_summary: activityData.conversationSummary || '',
    // Additional fields for compatibility and rich features
    priority: activityData.priority || 'medium',
    status: activityData.status || 'todo',
    completed: activityData.completed || false,
    ai_generated: activityData.aiGenerated || false,
    learning_goal: activityData.learningGoal || '',
    ai_reflection: activityData.aiReflection || null,
    scratch_pad: activityData.scratchPad || '',
    started_at: activityData.startedAt || null,
    estimated_minutes: activityData.estimatedMinutes || null,
    time_remaining_seconds: activityData.timeRemainingSeconds || null,
    time_spent: activityData.timeSpent || 0,
    timer_paused: activityData.timerPaused || false,
    associated_project_id: activityData.projectId || null,
    associated_course_id: activityData.courseId || null,
    // Cross-device sync fields
    client_id: activityData.clientId || null,
    client_version: activityData.clientVersion || null,
    is_dirty: false,
    // Rich card fields
    is_pinned: activityData.isPinned || false,
    progress: activityData.progress || 0,
    tags: activityData.tags || [],
    card_color: activityData.cardColor || null,
    icon: activityData.icon || null,
    sort_index: activityData.sortIndex || null,
    metadata: activityData.metadata || null,
  };

  const { data, error } = await supabase
    .from(activities_table_name)
    .insert([activity])
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[createActivity] Error creating activity:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[createActivity] Activity created successfully:', data.id);

  return { data };
};

/**
 * Update an existing activity
 * @param {string} activityId - Activity ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} { data: activity, error }
 */
export const updateActivity = async (activityId, updates) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  // Map frontend field names to database column names
  const dbUpdates = {};
  // New task_* schema fields
  if (updates.who !== undefined || updates.owner !== undefined)
    dbUpdates.task_owner = updates.owner || updates.who;
  if (updates.what !== undefined || updates.title !== undefined)
    dbUpdates.task_title = updates.title || updates.what;
  if (updates.description !== undefined || updates.taskDescription !== undefined)
    dbUpdates.task_description = updates.taskDescription || updates.description;
  if (updates.when !== undefined || updates.deadline !== undefined)
    dbUpdates.task_deadline = updates.deadline || updates.when;
  if (updates.urgencyLevel !== undefined) dbUpdates.task_urgency_level = updates.urgencyLevel;
  if (updates.reflection !== undefined) dbUpdates.task_reflection = updates.reflection;
  if (updates.conversationSummary !== undefined)
    dbUpdates.task_conversation_summary = updates.conversationSummary;

  // Additional fields
  if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.completed !== undefined) {
    dbUpdates.completed = updates.completed;
    // Set completed_at timestamp when marking as completed
    if (updates.completed) {
      dbUpdates.completed_at = new Date().toISOString();
    } else {
      dbUpdates.completed_at = null;
    }
  }
  if (updates.aiGenerated !== undefined) dbUpdates.ai_generated = updates.aiGenerated;
  if (updates.learningGoal !== undefined) dbUpdates.learning_goal = updates.learningGoal;
  if (updates.startedAt !== undefined) dbUpdates.started_at = updates.startedAt;
  if (updates.finishedAt !== undefined) dbUpdates.finished_at = updates.finishedAt;
  if (updates.estimatedMinutes !== undefined)
    dbUpdates.estimated_minutes = updates.estimatedMinutes;
  if (updates.timeRemainingSeconds !== undefined)
    dbUpdates.time_remaining_seconds = updates.timeRemainingSeconds;
  if (updates.projectId !== undefined) dbUpdates.associated_project_id = updates.projectId;
  if (updates.courseId !== undefined) dbUpdates.associated_course_id = updates.courseId;
  if (updates.scratchPad !== undefined) dbUpdates.scratch_pad = updates.scratchPad;
  if (updates.aiReflection !== undefined) dbUpdates.ai_reflection = updates.aiReflection;
  if (updates.timerPaused !== undefined) dbUpdates.timer_paused = updates.timerPaused;

  // Rich card fields
  if (updates.isPinned !== undefined) dbUpdates.is_pinned = updates.isPinned;
  if (updates.progress !== undefined) dbUpdates.progress = updates.progress;
  if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
  if (updates.cardColor !== undefined) dbUpdates.card_color = updates.cardColor;
  if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
  if (updates.sortIndex !== undefined) dbUpdates.sort_index = updates.sortIndex;
  if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

  // Sync fields
  if (updates.clientId !== undefined) dbUpdates.client_id = updates.clientId;
  if (updates.clientVersion !== undefined) dbUpdates.client_version = updates.clientVersion;
  if (updates.isDirty !== undefined) dbUpdates.is_dirty = updates.isDirty;
  if (updates.lastSyncedAt !== undefined) dbUpdates.last_synced_at = updates.lastSyncedAt;

  const { data, error } = await supabase
    .from(activities_table_name)
    .update(dbUpdates)
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[updateActivity] Error updating activity:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[updateActivity] Activity updated successfully:', activityId);

  return { data };
};

/**
 * Toggle activity completion status
 * @param {string} activityId - Activity ID
 * @param {string} reflection - Optional reflection text when completing
 * @returns {Promise<Object>} { data: activity, error }
 */
export const toggleActivityCompletion = async (activityId, reflection = null) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  // First, fetch the current activity to get its completion status
  const { data: activity, error: fetchError } = await supabase
    .from(activities_table_name)
    .select('completed')
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .single();

  if (fetchError) {
    // eslint-disable-next-line no-console
    console.error('[toggleActivityCompletion] Error fetching activity:', fetchError);
    return { error: fetchError };
  }

  const newCompletedStatus = !activity.completed;
  const updates = {
    completed: newCompletedStatus,
    completed_at: newCompletedStatus ? new Date().toISOString() : null,
    status: newCompletedStatus ? 'done' : 'todo',
  };

  // Add reflection if provided and marking as completed
  if (newCompletedStatus && reflection) {
    updates.task_reflection = reflection;
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update(updates)
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[toggleActivityCompletion] Error toggling completion:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[toggleActivityCompletion] Activity completion toggled:', activityId);

  return { data };
};

/**
 * Delete an activity
 * @param {string} activityId - Activity ID
 * @returns {Promise<Object>} { data, error }
 */
export const deleteActivity = async (activityId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .delete()
    .eq('id', activityId)
    .eq('user_id', session.user.id);

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[deleteActivity] Error deleting activity:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[deleteActivity] Activity deleted successfully:', activityId);

  return { data };
};

/**
 * Get activity statistics for the current user
 * @returns {Promise<Object>} { data: stats, error }
 */
export const getUserActivityStats = async () => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase.rpc('get_user_activity_stats', {
    p_user_id: session.user.id,
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[getUserActivityStats] Error fetching stats:', error);
    return { error };
  }

  return { data: data[0] || {} };
};

/**
 * Bulk create activities (useful for AI-generated activities)
 * @param {Array} activitiesData - Array of activity data objects
 * @returns {Promise<Object>} { data: activities[], error }
 */
export const createBulkActivities = async (activitiesData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const activities = activitiesData.map((activityData) => ({
    user_id: session.user.id,
    task_owner: activityData.who || activityData.owner || null,
    task_title: activityData.what || activityData.title,
    task_description: activityData.description || activityData.taskDescription || null,
    task_deadline: activityData.when || activityData.deadline,
    task_urgency_level: activityData.urgencyLevel || 'medium',
    task_reflection: activityData.reflection || null,
    task_conversation_summary: activityData.conversationSummary || null,
    priority: activityData.priority || 'medium',
    status: activityData.status || 'todo',
    completed: activityData.completed || false,
    ai_generated: activityData.aiGenerated || false,
    learning_goal: activityData.learningGoal || null,
    associated_project_id: activityData.projectId || null,
    associated_course_id: activityData.courseId || null,
  }));

  const { data, error } = await supabase.from(activities_table_name).insert(activities).select();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[createBulkActivities] Error creating activities:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[createBulkActivities] Activities created successfully:', data.length);

  return { data };
};

/**
 * Fetch activities by urgency level
 * @param {string} urgencyLevel - Urgency level: 'critical', 'high', 'medium', 'low'
 * @param {boolean} completedOnly - Optional: filter by completion status
 * @returns {Promise<Object>} { data: activities[], error }
 */
export const fetchActivitiesByUrgency = async (urgencyLevel, completedOnly = false) => {
  const filters = { urgencyLevel };
  if (completedOnly !== undefined) {
    filters.completed = completedOnly;
  }
  return fetchUserActivities(filters);
};

/**
 * Update activity urgency level
 * @param {string} activityId - Activity ID
 * @param {string} urgencyLevel - New urgency level: 'critical', 'high', 'medium', 'low'
 * @returns {Promise<Object>} { data: activity, error }
 */
export const updateActivityUrgency = async (activityId, urgencyLevel) => {
  return updateActivity(activityId, { urgencyLevel });
};

/**
 * Mark activity as started
 * @param {string} activityId - Activity ID
 * @returns {Promise<Object>} { data: activity, error }
 */
export const startActivity = async (activityId) => {
  return updateActivity(activityId, { startedAt: new Date().toISOString() });
};

/**
 * Update activity timer state
 * @param {string} activityId - Activity ID
 * @param {number} estimatedMinutes - Estimated minutes for the task
 * @param {number} timeRemainingSeconds - Time remaining in seconds
 * @returns {Promise<Object>} { data: activity, error }
 */
export const updateActivityTimer = async (activityId, estimatedMinutes, timeRemainingSeconds) => {
  return updateActivity(activityId, {
    estimatedMinutes,
    timeRemainingSeconds,
  });
};

/**
 * Update task priority
 * @param {string} activityId - Activity ID
 * @param {string} priority - Priority level: 'low', 'medium', 'high', 'urgent'
 * @returns {Promise<Object>} { data: activity, error }
 */
export const updateTaskPriority = async (activityId, priority) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({ priority })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[updateTaskPriority] Error updating priority:', error);
    return { error };
  }

  return { data };
};

/**
 * Update time spent on task
 * @param {string} activityId - Activity ID
 * @param {number} timeSpentSeconds - Time spent in seconds
 * @returns {Promise<Object>} { data: activity, error }
 */
export const updateTaskTimeSpent = async (activityId, timeSpentSeconds) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({ time_spent: timeSpentSeconds })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[updateTaskTimeSpent] Error updating time spent:', error);
    return { error };
  }

  return { data };
};

/**
 * Save AI-generated task reflection
 * @param {string} activityId - Activity ID
 * @param {Object} reflectionData - Reflection data from AI
 * @returns {Promise<Object>} { data: activity, error }
 */
export const saveTaskReflection = async (activityId, reflectionData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const normalized = normalizeTaskReflectionPayload(reflectionData || {});

  const updates = {
    ai_reflection: normalized.aiReflection,
    reflection_generated_at: new Date().toISOString(),
    reflection_insights: reflectionData.insights || normalized.insights || [],
    reflection_patterns: reflectionData.patterns || normalized.patterns || [],
    reflection_recommendations: reflectionData.recommendations || normalized.recommendations || [],
    reflection_depth_score: reflectionData.depthScore || null,
    reflection_emotional_tone: reflectionData.emotionalTone || normalized.emotionalTone || null,
    key_learnings: reflectionData.keyLearnings || normalized.keyLearnings || [],
    challenges_faced: reflectionData.challengesFaced || [],
    solutions_applied: reflectionData.solutionsApplied || [],
    skills_practiced: reflectionData.skillsPracticed || [],
  };

  const { data, error } = await supabase
    .from(activities_table_name)
    .update(updates)
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[saveTaskReflection] Error saving reflection:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[saveTaskReflection] Reflection saved successfully:', activityId);

  return { data };
};

/**
 * Save user's task reflection responses
 * @param {string} activityId - Activity ID
 * @param {Object} responses - User's reflection responses
 * @returns {Promise<Object>} { data: activity, error }
 */
export const saveUserTaskReflectionResponses = async (activityId, responses) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({ user_reflection_responses: responses })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[saveUserTaskReflectionResponses] Error saving responses:', error);
    return { error };
  }

  return { data };
};

/**
 * Save or update task scratch pad notes
 * @param {string} activityId - Activity ID
 * @param {string} notes - Scratch pad notes/content
 * @returns {Promise<Object>} { data: activity, error }
 */
export const saveTaskScratchPad = async (activityId, notes) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({ scratch_pad: notes })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[saveTaskScratchPad] Error saving scratch pad:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[saveTaskScratchPad] Scratch pad saved successfully');
  return { data };
};

/**
 * Save conversation summary for a task
 * @param {string} activityId - Activity ID
 * @param {string} summary - Conversation summary text
 * @returns {Promise<Object>} { data: activity, error }
 */
export const saveConversationSummary = async (activityId, summary) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({ conversation_summary: summary })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[saveConversationSummary] Error saving conversation summary:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[saveConversationSummary] Conversation summary saved successfully');
  return { data };
};

/**
 * Save user's manual reflection notes
 * @param {string} activityId - Activity ID
 * @param {string} reflectionText - User's reflection text
 * @returns {Promise<Object>} { data: activity, error }
 */
export const saveManualReflection = async (activityId, reflectionText) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({ reflection_notes: reflectionText })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[saveManualReflection] Error saving reflection:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[saveManualReflection] Reflection saved successfully');
  return { data };
};

/**
 * Save AI-generated reflection (updates ai_reflection column)
 * @param {string} activityId - Activity ID
 * @param {string} aiReflectionText - AI-generated reflection text
 * @returns {Promise<Object>} { data: activity, error }
 */
export const saveAIReflection = async (activityId, aiReflectionText) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const normalizedText = normalizeTaskReflectionText(aiReflectionText);

  const { data, error } = await supabase
    .from(activities_table_name)
    .update({
      ai_reflection: normalizedText,
      reflection_generated_at: new Date().toISOString(),
    })
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[saveAIReflection] Error saving AI reflection:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[saveAIReflection] AI reflection saved successfully');
  return { data };
};

/**
 * Update task completion with reflection and metrics
 * @param {string} activityId - Activity ID
 * @param {Object} completionData - Completion data including reflection
 * @returns {Promise<Object>} { data: activity, error }
 */
export const completeTaskWithReflection = async (activityId, completionData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const updates = {
    completed: true,
    completed_at: new Date().toISOString(),
    task_outcome: completionData.taskOutcome || 'completed',
    completion_percentage: completionData.completionPercentage || 100,
    productivity_rating: completionData.productivityRating || null,
    focus_quality: completionData.focusQuality || null,
  };

  // Add reflection if provided
  if (completionData.reflection) {
    updates.reflection_notes = completionData.reflection;
  }

  // Add user reflection responses if provided
  if (completionData.userReflectionResponses) {
    updates.user_reflection_responses = completionData.userReflectionResponses;
  }

  const { data, error } = await supabase
    .from(activities_table_name)
    .update(updates)
    .eq('id', activityId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[completeTaskWithReflection] Error completing task:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[completeTaskWithReflection] Task completed with reflection:', activityId);

  return { data };
};

/**
 * Get tasks with reflections for a user
 * @param {Object} filters - Optional filters
 * @returns {Promise<Object>} { data: activities[], error }
 */
export const getTasksWithReflections = async (filters = {}) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  let query = supabase
    .from(activities_table_name)
    .select('*')
    .eq('user_id', session.user.id)
    .not('ai_reflection', 'is', null)
    .order('reflection_generated_at', { ascending: false });

  // Apply filters
  if (filters.limit) {
    query = query.limit(filters.limit);
  }

  if (filters.startDate) {
    query = query.gte('reflection_generated_at', filters.startDate);
  }

  if (filters.endDate) {
    query = query.lte('reflection_generated_at', filters.endDate);
  }

  const { data, error } = await query;

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[getTasksWithReflections] Error fetching tasks:', error);
    return { error };
  }

  return { data };
};

/**
 * Subscribe to real-time changes for user's activities
 * @param {string} userId - User ID to subscribe to
 * @param {Function} onInsert - Callback for new activities
 * @param {Function} onUpdate - Callback for updated activities
 * @param {Function} onDelete - Callback for deleted activities
 * @returns {Object} Subscription object with unsubscribe method
 */
export const subscribeToActivities = (userId, { onInsert, onUpdate, onDelete }) => {
  if (!userId) {
    // eslint-disable-next-line no-console
    console.error('[subscribeToActivities] User ID is required');
    return null;
  }

  // Check if subscription already exists
  if (activeSubscriptions.has(userId)) {
    // eslint-disable-next-line no-console
    console.log('[subscribeToActivities] Subscription already exists for user:', userId);
    return activeSubscriptions.get(userId);
  }

  // eslint-disable-next-line no-console
  console.log('[subscribeToActivities] Creating subscription for user:', userId);

  const subscription = supabase
    .channel(`activities_${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: activities_table_name,
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        // eslint-disable-next-line no-console
        console.log('[subscribeToActivities] INSERT event:', payload.new);
        if (onInsert) onInsert(payload.new);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: activities_table_name,
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        // eslint-disable-next-line no-console
        console.log('[subscribeToActivities] UPDATE event:', payload.new);
        if (onUpdate) onUpdate(payload.new, payload.old);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: activities_table_name,
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        // eslint-disable-next-line no-console
        console.log('[subscribeToActivities] DELETE event:', payload.old);
        if (onDelete) onDelete(payload.old);
      }
    )
    .subscribe((status) => {
      // eslint-disable-next-line no-console
      console.log('[subscribeToActivities] Subscription status:', status);
    });

  // Store subscription
  const subscriptionWrapper = {
    subscription,
    unsubscribe: async () => {
      // eslint-disable-next-line no-console
      console.log('[subscribeToActivities] Unsubscribing for user:', userId);
      await supabase.removeChannel(subscription);
      activeSubscriptions.delete(userId);
    },
  };

  activeSubscriptions.set(userId, subscriptionWrapper);
  return subscriptionWrapper;
};

/**
 * Unsubscribe from activity changes for a specific user
 * @param {string} userId - User ID to unsubscribe
 */
export const unsubscribeFromActivities = async (userId) => {
  const subscription = activeSubscriptions.get(userId);
  if (subscription) {
    await subscription.unsubscribe();
  }
};

/**
 * Unsubscribe from all activity subscriptions
 */
export const unsubscribeFromAllActivities = async () => {
  // eslint-disable-next-line no-console
  console.log('[unsubscribeFromAllActivities] Cleaning up all subscriptions');
  const promises = Array.from(activeSubscriptions.values()).map((sub) => sub.unsubscribe());
  await Promise.all(promises);
  activeSubscriptions.clear();
};
